import { type FormEvent, useState } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { authCopy } from '@/copy'
import { Alert } from '@/ui/Alert/Alert'
import { Button } from '@/ui/Button/Button'
import { TextField } from '@/ui/Field/Field'
import { Stack } from '@/ui/layout/Stack'
import { acceptInvite, login } from '../api/auth'
import { AuthShell } from '../components/AuthShell'
import { ROUTES } from '../constants/routes'
import { useAsyncSubmit } from '../hooks/useAsyncSubmit'
import { useAuthStore } from '../store/auth'

export function AcceptInvitePage() {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const navigate = useNavigate()
  const setAuth = useAuthStore((s) => s.setAuth)

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const { loading, error, setError, submit } = useAsyncSubmit()
  const [fieldError, setFieldError] = useState<{
    field: 'password' | 'confirm'
    message: string
  } | null>(null)

  if (!token) {
    return <Navigate to={ROUTES.login} replace />
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setFieldError(null)
    setError(null)
    if (password.length < 8) {
      setFieldError({
        field: 'password',
        message: authCopy.passwordTooShort,
      })
      return
    }
    if (password !== confirm) {
      setFieldError({ field: 'confirm', message: authCopy.passwordMismatch })
      return
    }
    await submit(async () => {
      await acceptInvite(token, username, password)
      const res = await login(username, password)
      setAuth(res.token, res.user_id, res.username, res.is_system_admin)
      void navigate(ROUTES.home, { replace: true })
    }, authCopy.invite.failed)
  }

  return (
    <AuthShell heading={authCopy.invite.heading}>
      <Stack as="form" gap="space-16" onSubmit={(e) => void handleSubmit(e)}>
        <TextField
          id="username"
          label={authCopy.username}
          value={username}
          onValue={setUsername}
          inputProps={{
            type: 'text',
            autoComplete: 'username',
            required: true,
          }}
        />
        <TextField
          id="password"
          label={authCopy.password}
          value={password}
          onValue={setPassword}
          error={
            fieldError?.field === 'password' ? fieldError.message : undefined
          }
          inputProps={{
            type: 'password',
            autoComplete: 'new-password',
            required: true,
          }}
        />
        <TextField
          id="confirm"
          label={authCopy.confirmPassword}
          value={confirm}
          onValue={setConfirm}
          error={
            fieldError?.field === 'confirm' ? fieldError.message : undefined
          }
          inputProps={{
            type: 'password',
            autoComplete: 'new-password',
            required: true,
          }}
        />
        {error && <Alert tone="danger">{error}</Alert>}
        <Button
          type="submit"
          variant="primary"
          disabled={!username || !password || !confirm}
          loading={loading}
        >
          {authCopy.invite.submit}
        </Button>
      </Stack>
    </AuthShell>
  )
}
